import { useContext, useState, useEffect, useMemo } from "react";
import NProgress from "nprogress";
import { Box } from "@mui/material";
import { StriimButton, StriimTooltip, StriimTypography, StriimCircularLoader } from "@striim/striim-ui";
import Stepper from "../../../common/stepper/stepper";
import { Steps, getCurrentStepHeader, getCurrentStepSubHeader, stepDetails } from "../../target-steps";
import Header from "../../../common/header/header";
import Footer from "../../../common/footer/footer";
import { WizardContext } from "../../../wizard-context";
import TableEditComponent from "./editable-target-table";
import WizardLayout from "../../../common/wizard-layout/wizard-layout";
import growl from "../../../../../../app/components/common/growl";
import sanitizer from "../../../../../../app/components/appwizard/component-wizards/wizards/target/base-simple-target/server-error-sanitizer.js";
import {
    getTargetTables,
    storeTargetTableInWizardContext,
    getTargetTablesFromWizardContext
} from "../../../services/target-tables-helper";
import { targetUtils } from "../../../utils";
import Tracker from "../../../../../../core/services/tracker/tracker";
import { TRACKER_STRINGS } from "../../../../../../core/services/tracker/constants";
import { InfoEdit } from "../../../../../generic/icon/customIcons";
import { Reset } from "../../../../../generic/icon/customIcons";
import { SvgIcon } from "@mui/material";
import { styles } from "../../../common/review/review.styles";
import { isAutomatedWizard } from "../../../../../../app/components/appwizard/component-wizards/wizards/source/database-reader/app-services";
import { routeHistory } from "../../../../../hooks/useNavigationSecurity";
import ComponentNames from "../../../../../../resources/generated/components.json";
import { ComponentKeys } from "../../../../../../core/services/componentService/component-service";

interface ITableMappingEditorProps {
    target;
}

export interface TablesState {
    value: string;
    changed: boolean;
    error: boolean;
    disabled: boolean;
}

const defaultTableProps: TablesState = {
    value: "",
    changed: false,
    error: false,
    disabled: true
};

export default function TableMappingEditor(props: ITableMappingEditorProps) {
    const {
        wizardAppName,
        setCurrentStep,
        sourceInfo,
        targetInfo,
        setTargetInfo,
        cdcSourceInfo,
        cdcTargetInfo,
        setCdcTargetInfo,
        isAutomatedILCDC,
        wizardType
    } = useContext(WizardContext);
    const [ilTablesProperty, setIlTablesProperty] = useState<TablesState>(defaultTableProps);
    const [cdcTablesProperty, setCdcTablesProperty] = useState<TablesState>(defaultTableProps);
    const [isFooterBtnDisabled, setIsFooterBtnDisabled] = useState<boolean>(false);
    const [showSeparateTables, setShowSeparateTables] = useState<boolean>(false);
    const [isLoadingTables, setIsLoadingTables] = useState<boolean>(true);
    const { target } = props;
    const targetDisplayName = ComponentNames[target][ComponentKeys.HumanReadableName];
    const heading = getCurrentStepHeader(Steps.TableMappingEditor, targetDisplayName);
    const subHeading = getCurrentStepSubHeader(Steps.TableMappingEditor, targetDisplayName);
    const activeStep = stepDetails.ConfigureTarget.id;
    const MemoizedStepper = useMemo(() => <Stepper steps={stepDetails} activeStep={activeStep} />, [
        stepDetails,
        activeStep
    ]);

    async function setTablesProperty(reset = false) {
        const previousPage = routeHistory.previous?.split("/").pop();
        const isReviewPage = previousPage === stepDetails.Review.stepName;
        const disabled = reset || !isReviewPage;
        // if user resets , data is fetched from saved target model, if not , fetched from wizard context using targetInfo
        let tables = reset
            ? await getTargetTables(sourceInfo?.appId)
            : await getTargetTablesFromWizardContext(sourceInfo?.appId, targetInfo);

        setIlTablesProperty({
            ...defaultTableProps,
            value: tables,
            disabled,
            changed: reset
        });

        if (isAutomatedWizard()) {
            // if user resets , data is fetched from saved target model, if not , fetched from wizard context using cdcTargetInfo
            let cdcTables = reset
                ? await getTargetTables(cdcSourceInfo?.appId)
                : await getTargetTablesFromWizardContext(cdcSourceInfo?.appId, cdcTargetInfo);

            setCdcTablesProperty({
                ...defaultTableProps,
                value: cdcTables,
                disabled,
                changed: reset
            });
            if (tables !== cdcTables) {
                setShowSeparateTables(true);
            }
        }
        setIsLoadingTables(false);
    }
    useEffect(() => {
        setTablesProperty();
    }, []);

    const WizardHeader = (
        <Header
            heading={heading}
            subHeading={subHeading}
            rightContent={
                <div style={{ display: "inline" }}>
                    <StriimTooltip title="Please use caution when changing this property. Wrong input might result in unexpected behaviour of the apps">
                        <StriimButton
                            variant="text"
                            sx={{ gap: 0.5 }}
                            data-testid="edit-table-mapping"
                            onClick={() => {
                                setIlTablesProperty(prev => ({ ...prev, disabled: false }));
                                if (isAutomatedWizard()) {
                                    setCdcTablesProperty(prev => ({ ...prev, disabled: false }));
                                }
                            }}
                        >
                            <SvgIcon component={InfoEdit} sx={styles.editIconSmall} />
                            Edit
                        </StriimButton>
                    </StriimTooltip>
                    <StriimButton
                        variant="text"
                        sx={{ gap: 0.5 }}
                        onClick={() => setTablesProperty(true)}
                        data-testid="reset-table-mapping"
                    >
                        <SvgIcon component={Reset} sx={styles.editIconSmall} />
                        Reset
                    </StriimButton>
                </div>
            }
        />
    );

    const trackEvent = buttonClicked => {
        Tracker.getInstance().track(
            TRACKER_STRINGS.schema.wizards,
            {
                id: sourceInfo["appId"],
                event: TRACKER_STRINGS.eventClicked.wizards,
                step: stepDetails.TableMappingEditor.stepName,
                adapterType: isAutomatedILCDC ? cdcSourceInfo?.adapterName : sourceInfo?.adapterName,
                buttonClicked: buttonClicked,
                isAutomatedILCDC: isAutomatedILCDC
            },
            TRACKER_STRINGS.version.withModification
        );
    };

    const handleBack = () => {
        setCurrentStep(Steps.ConfigureTarget);
        trackEvent("Back");
    };

    const handleNext = async () => {
        try {
            setIsFooterBtnDisabled(true);
            NProgress.start();

            const prevIlTables = await getTargetTablesFromWizardContext(sourceInfo?.appId, targetInfo);
            const prevCDCTables = await getTargetTablesFromWizardContext(cdcSourceInfo?.appId, cdcTargetInfo);

            if (ilTablesProperty.changed) {
                if (ilTablesProperty.value === "") {
                    setIlTablesProperty(prev => ({ ...prev, error: true }));
                    return;
                }
                await storeTargetTableInWizardContext(sourceInfo?.appId, setTargetInfo, ilTablesProperty.value);
            }
            // if Il and cdc table mapping are same, only the il table mapping will be shown
            // save the cdc target with il table mapping
            if (isAutomatedILCDC && prevCDCTables === prevIlTables && ilTablesProperty.changed) {
                await storeTargetTableInWizardContext(cdcSourceInfo?.appId, setCdcTargetInfo, ilTablesProperty.value);
            }
            if (cdcTablesProperty.changed) {
                if (cdcTablesProperty.value === "") {
                    setCdcTablesProperty(prev => ({ ...prev, error: true }));
                    return;
                }
                await storeTargetTableInWizardContext(cdcSourceInfo?.appId, setCdcTargetInfo, cdcTablesProperty.value);
            }
            if (targetUtils.skipTargetValidation(target)) {
                setCurrentStep(Steps.Review);
            } else {
                setCurrentStep(Steps.ValidateTarget);
            }
        } catch (e) {
            let msg = "Please correct the errors and try again.";
            if (Array.isArray(e)) {
                msg += e.join(", ");
            } else {
                msg += sanitizer.sanitize(e.message);
            }
            growl.error(msg, "Error saving your target.");
        } finally {
            NProgress.done();
            setIsFooterBtnDisabled(false);
        }
    };
    return (
        <WizardLayout
            Header={WizardHeader}
            Stepper={MemoizedStepper}
            inlineDocKey={"table-mapping-editor"}
            Footer={
                <Footer
                    onNext={() => {
                        handleNext();
                    }}
                    onBack={() => {
                        handleBack();
                    }}
                    hasBack={true}
                    backDisabled={isFooterBtnDisabled}
                    nextDisabled={isFooterBtnDisabled}
                />
            }
        >
            <Box minHeight={"40vh"}>
                {isLoadingTables ? (
                    <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                        minHeight="40vh"
                        gap={2}
                    >
                        <StriimCircularLoader size={20} thickness={8} />
                        <StriimTypography variant="body4" color="greyscale.700">
                            Loading table mapping...
                        </StriimTypography>
                    </Box>
                ) : (
                    <TableEditComponent
                        ilTablesProperty={ilTablesProperty}
                        setIlTablesProperty={setIlTablesProperty}
                        cdcTablesProperty={cdcTablesProperty}
                        setCdcTablesProperty={setCdcTablesProperty}
                        showSeparateTables={showSeparateTables}
                    ></TableEditComponent>
                )}
            </Box>
        </WizardLayout>
    );
}
